import { FormControl, FormErrorMessage, FormLabel, Select, useToast } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import type { DTO } from "@equidefi/shared/types/dto";

// @ts-ignore
import { MESSAGE } from "@equidefi/constants/forms";
import { JOINT_SUB_TYPE_KEYS, JOINT_SUB_TYPE_OPTIONS } from "@equidefi/constants/profile";
import { SavedProfileFormContainer } from "./SavedProfileFormContainer";

const schema = z.object({
  ta_sub_type: z.enum(JOINT_SUB_TYPE_KEYS, {
    message: MESSAGE.REQUIRED("Joint Sub-type"),
  }),
});

export type JointAccountTypeFormValues = z.infer<typeof schema>;

type JointAccountTypeFormProps = {
  profile: DTO.SavedProfile;
  onSave: (values: JointAccountTypeFormValues) => Promise<void>;
};

const getInitialValues = (profile: DTO.SavedProfile) => ({
  ta_sub_type: profile.ta_sub_type ?? "",
});

export function JointAccountTypeForm({ profile, onSave }: JointAccountTypeFormProps) {
  const toast = useToast();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<JointAccountTypeFormValues>({
    defaultValues: getInitialValues(profile),
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const onSubmit = useCallback(
    async (values: JointAccountTypeFormValues) => {
      try {
        await onSave({ ta_sub_type: values.ta_sub_type });
      } catch (error) {
        console.error(error);

        toast({
          status: "error",
          description: "There was an error trying to save your joint account type.",
        });
      }
    },
    [onSave, toast],
  );

  return (
    <SavedProfileFormContainer
      profile={profile}
      title="Account Type"
      onSave={handleSubmit(onSubmit)}
      isLoading={isSubmitting}
      isDisabled={!isValid}>
      <Controller
        name="ta_sub_type"
        control={control}
        render={({ field, fieldState }) => (
          <FormControl isRequired isInvalid={!!fieldState.error}>
            <FormLabel>Joint Sub-type</FormLabel>

            <Select
              placeholder="Select Sub-type"
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              bg="white">
              {JOINT_SUB_TYPE_OPTIONS.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>

            {fieldState.error && <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>}
          </FormControl>
        )}
      />
    </SavedProfileFormContainer>
  );
}
